import { JsonFormat, JsonFormatError } from "@redotech/json/format";
import { Json } from "@redotech/json/json";
import { isDef } from "@redotech/util/checks";
import { z } from "zod";

const iso8601Regex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const iso8601DateString = z
  .string()
  .refine((value) => iso8601Regex.test(value), {
    message: "Invalid ISO 8601 date format",
  });

const urlSchema = z.string().url();

const MoneySetSchema = z.object({
  amount: z.string(),
  currency_code: z.string(),
});

const OptionalMoneySetSchema = z
  .object({ shop_money: MoneySetSchema, presentment_money: MoneySetSchema })
  .nullish();

const CustomerAddressSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  company: z.string().nullish(),
  country: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
  name: z.string().nullish(),
  province_code: z.string().nullish(),
  country_code: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});

const ClientDetailsSchema = z.object({
  accept_language: z.string().nullish(),
  browser_height: z.number().nullish(),
  browser_ip: z.string().nullish(),
  browser_width: z.number().nullish(),
  session_hash: z.string().nullish(),
  user_agent: z.string().nullish(),
});

const DiscountApplicationSchemaAllocationMethodEnum = z.enum([
  "across",
  "each",
  "one",
]);
const DiscountApplicationSchemaTargetSelectionEnum = z.enum([
  "all",
  "entitled",
  "explicit",
]);
const DiscountApplicationSchemaTargetTypeEnum = z.enum([
  "line_item",
  "shipping_line",
]);
const DiscountApplicationSchemaTypeEnum = z.enum([
  "automatic",
  "manual",
  "script",
  "discount_code",
]);
const DiscountApplicationValueTypeEnum = z.enum(["fixed_amount", "percentage"]);
const DiscountApplicationSchema = z.object({
  allocation_method: DiscountApplicationSchemaAllocationMethodEnum,
  code: z.string().nullish(),
  description: z.string().nullish(),
  target_selection: DiscountApplicationSchemaTargetSelectionEnum,
  target_type: DiscountApplicationSchemaTargetTypeEnum,
  title: z.string().nullish(),
  type: DiscountApplicationSchemaTypeEnum,
  value_type: DiscountApplicationValueTypeEnum,
  value: z.string(),
});

const DiscountCodeValueTypeEnum = z.enum([
  "fixed_amount",
  "percentage",
  "shipping",
]);
const DiscountCodeSchema = z.object({
  amount: z.string(),
  code: z.string(),
  type: DiscountCodeValueTypeEnum,
});

const TaxLineSchema = z.object({
  title: z.string(),
  price: z.string(),
  rate: z.number(),
  price_set: z
    .object({ shop_money: MoneySetSchema, presentment_money: MoneySetSchema })
    .nullish(),
  channel_liable: z.boolean().nullish(),
});

const CompleteOrderFulfillmentStatusZodSchema = z.object({
  _value: z.string(),
  _dbValue: z.string().nullable(),
  _displayValue: z.string(),
});

/**
 * In OrderFulfillmentStatusEnum, unfulfilled is represented as null. This
 * CompleteOrderFulfillmentStatus enum is for convenience.
 */
export class CompleteOrderFulfillmentStatus {
  public static readonly FULFILLED = new CompleteOrderFulfillmentStatus(
    "fulfilled",
    "fulfilled",
    "Fulfilled",
  );
  public static readonly RESTOCKED = new CompleteOrderFulfillmentStatus(
    "restocked",
    "restocked",
    "Restocked",
  );
  public static readonly PARTIAL = new CompleteOrderFulfillmentStatus(
    "partial",
    "partial",
    "Partial",
  );
  public static readonly NOT_ELIGIBLE = new CompleteOrderFulfillmentStatus(
    "not_eligible",
    "not_eligible",
    "Not eligible",
  );
  public static readonly UNFULFILLED = new CompleteOrderFulfillmentStatus(
    "unfulfilled",
    null,
    "Unfulfilled",
  );
  public static readonly CANCELED = new CompleteOrderFulfillmentStatus(
    "canceled",
    "canceled",
    "Canceled",
  );

  public static readonly allValues = [
    CompleteOrderFulfillmentStatus.FULFILLED.value,
    CompleteOrderFulfillmentStatus.RESTOCKED.value,
    CompleteOrderFulfillmentStatus.PARTIAL.value,
    CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.value,
    CompleteOrderFulfillmentStatus.UNFULFILLED.value,
    CompleteOrderFulfillmentStatus.CANCELED.value,
  ];

  public static readonly allDbValues = [
    CompleteOrderFulfillmentStatus.FULFILLED.dbValue,
    CompleteOrderFulfillmentStatus.RESTOCKED.dbValue,
    CompleteOrderFulfillmentStatus.PARTIAL.dbValue,
    CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.dbValue,
    CompleteOrderFulfillmentStatus.UNFULFILLED.dbValue,
    CompleteOrderFulfillmentStatus.CANCELED.dbValue,
  ];

  public static fromValue(value: string): CompleteOrderFulfillmentStatus {
    switch (value) {
      case CompleteOrderFulfillmentStatus.FULFILLED.value:
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case CompleteOrderFulfillmentStatus.RESTOCKED.value:
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case CompleteOrderFulfillmentStatus.PARTIAL.value:
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.value:
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      case CompleteOrderFulfillmentStatus.UNFULFILLED.value:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case CompleteOrderFulfillmentStatus.CANCELED.value:
        return CompleteOrderFulfillmentStatus.CANCELED;
      default:
        throw new Error(
          `Invalid CompleteOrderFulfillmentStatus value ${value}`,
        );
    }
  }

  public static fromDbValue(
    dbValue: string | null | undefined,
  ): CompleteOrderFulfillmentStatus | undefined {
    switch (dbValue) {
      case CompleteOrderFulfillmentStatus.FULFILLED.dbValue:
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case CompleteOrderFulfillmentStatus.RESTOCKED.dbValue:
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case CompleteOrderFulfillmentStatus.PARTIAL.dbValue:
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.dbValue:
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      case CompleteOrderFulfillmentStatus.UNFULFILLED.dbValue:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case CompleteOrderFulfillmentStatus.CANCELED.dbValue:
        return CompleteOrderFulfillmentStatus.CANCELED;
      default:
        return undefined;
    }
  }

  public static jsonFormat: JsonFormat<CompleteOrderFulfillmentStatus> = {
    read(json: Json): CompleteOrderFulfillmentStatus {
      if (
        typeof json === "string" &&
        CompleteOrderFulfillmentStatus.allValues.includes(json)
      ) {
        return CompleteOrderFulfillmentStatus.fromValue(json)!;
      } else {
        throw new JsonFormatError(
          `Invalid CompleteOrderFulfillmentStatus ${json}`,
        );
      }
    },
    write(orderFulfillmentStatus: CompleteOrderFulfillmentStatus): Json {
      return orderFulfillmentStatus.value;
    },
  };

  /**
   * Attempts to parse as an object first and then as a nullable string so the
   * RPC layer doesn't have to have a different schema than the db layer.
   */
  public static zodParser = CompleteOrderFulfillmentStatusZodSchema.transform(
    (obj) => {
      return CompleteOrderFulfillmentStatus.fromValue(obj._value);
    },
  ).or(
    z
      .string()
      .nullable()
      .transform(
        (dbValue: string | null) =>
          CompleteOrderFulfillmentStatus.fromDbValue(dbValue)!,
      )
      .refine((value) => isDef(value), {
        message: "Unable to parse CompleteOrderFulfillmentStatus from string",
      }),
  );

  public static fromShopifyOrder(
    shopifyOrder: Pick<ShopifyOrder, "cancelled_at" | "fulfillment_status">,
  ): CompleteOrderFulfillmentStatus | undefined {
    if (shopifyOrder.cancelled_at) {
      return CompleteOrderFulfillmentStatus.CANCELED;
    }

    switch (shopifyOrder.fulfillment_status) {
      case null:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case "fulfilled":
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case "restocked":
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case "partial":
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case "not_eligible":
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      default:
        return undefined;
    }
  }

  private constructor(
    private readonly _value: string,
    private readonly _dbValue: string | null,
    private readonly _displayValue: string,
  ) {}

  public get value(): string {
    return this._value;
  }

  public get dbValue(): string | null {
    return this._dbValue;
  }

  public get displayValue(): string {
    return this._displayValue;
  }
}

export const OrderFulfillmentStatusEnum = z.enum([
  "fulfilled",
  "unfulfilled",
  "restocked",
  "partial",
  "not_eligible",
]);

const PropertySchema = z.object({
  name: z.string().nullish(),
  value: z.any().nullish(),
});

const LineItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  current_quantity: z.number().nullish(),
  tip_payment_gateway: z.string().nullish(),
  tip_payment_method: z.string().nullish(),
  image: z
    .object({
      id: z.number(),
      product_id: z.union([z.number(), z.string()]),
      position: z.number(),
      created_at: iso8601DateString,
      updated_at: iso8601DateString,
      alt: z.string().nullish(),
      width: z.number().nullish(),
      height: z.number().nullish(),
      src: z.string(),
      variant_ids: z.array(z.number()),
      admin_graphql_api_id: z.string(),
    })
    .nullish(),
  protected: z.union([z.boolean(), z.array(z.string())]).nullish(), // sometimes this is a boolean, others it is an array like ["re:do"] idk why
  finalSaleProtected: z.boolean().nullish(),
  tags: z.array(z.string()).nullish(),
  tip: z
    .object({
      payment_method: z.string().nullish(),
      payment_gateway: z.string().nullish(),
    })
    .nullish(),
  attributed_staffs: z
    .array(z.object({ id: z.string(), quantity: z.number() }))
    .nullish(),
  admin_graphql_api_id: z.string(),
  variant_id: z.union([z.number(), z.string()]).nullish(),
  title: z.string(),
  quantity: z.number(),
  price: z.string(),
  price_set: OptionalMoneySetSchema,
  pre_tax_price: z.string().nullish(),
  pre_tax_price_set: OptionalMoneySetSchema,
  grams: z.number(),
  sku: z.string().nullish(),
  vendor: z.string().nullish(),
  variant_title: z.string().nullish(),
  fulfillment_service: z.string(),
  product_id: z.union([z.number(), z.string()]).nullish(),
  requires_shipping: z.boolean(),
  taxable: z.boolean(),
  gift_card: z.boolean(),
  name: z.string(),
  variant_inventory_management: z.string().nullish(),
  properties: z.array(PropertySchema),
  product_exists: z.boolean(),
  fulfillable_quantity: z.number(),
  total_discount: z.string(),
  fulfillment_status: OrderFulfillmentStatusEnum.nullish(),
  total_discount_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  destination_location: z
    .object({
      id: z.number(),
      country_code: z.string().nullish(),
      province_code: z.string().nullish(),
      name: z.string().nullish(),
      address1: z.string().nullish(),
      address2: z.string().nullish(),
      city: z.string().nullish(),
      zip: z.string().nullish(),
    })
    .nullish(),
  origin_location: z
    .object({
      id: z.number(),
      country_code: z.string(),
      province_code: z.string(),
      name: z.string(),
      address1: z.string(),
      address2: z.string().nullish(),
      city: z.string(),
      zip: z.string(),
    })
    .nullish(),
  discount_allocations: z.array(
    z.object({
      amount: z.string(),
      discount_application_index: z.number(),
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  fulfillment_line_item_id: z.string().nullish(),
  tax_code: z.string().nullish(),
  tax_lines: z.array(TaxLineSchema),
  duties: z.array(
    z.object({
      id: z.number(),
      harmonized_system_code: z.string().nullish(),
      country_code_of_origin: z.string().nullish(),
      price_set: OptionalMoneySetSchema,
      tax_lines: z.array(TaxLineSchema),
      admin_graphql_api_id: z.string(),
    }),
  ),
  discount_price: z.string().nullish().optional(),
  reason: z.string().nullish().optional(),
  loyaltyAmount: z.number().nullish(),
  duties_included: z.any(),
  merchant_business_entity_id: z.any(),
  total_cash_rounding_payment_adjustment_set: z.any(),
  total_cash_rounding_refund_adjustment_set: z.any(),
  redoEligible: z.boolean().nullish(), // Not from Shopify, added by redo
  redoPackageProtectionEligible: z.boolean().nullish(), // Not from Shopify, added by redo
});

const ShipmentStatusEnum = z.enum([
  "label_printed",
  "label_purchased",
  "attempted_delivery",
  "ready_for_pickup",
  "confirmed",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "failure",
  "picked_up",
  "stalled_in_transit",
  "delayed",
]);
const FulfillmentStatusEnum = z.enum([
  "pending",
  "open",
  "success",
  "cancelled",
  "error",
  "failure",
]);
export const OriginAddressSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  country_code: z.string().nullish(),
  province_code: z.string().nullish(),
  zip: z.string().nullish(),
});

const FulfillmentSchema = z.object({
  admin_graphql_api_id: z.string().nullish(),
  created_at: iso8601DateString,
  id: z.union([z.number(), z.string(), z.null()]), // null for CS orders
  line_items: z.array(LineItemSchema),
  location_id: z.number().nullish(),
  location: z.object({ name: z.string().nullish() }).nullish(),
  name: z.string().nullish(),
  order_id: z.number(),
  origin_address: OriginAddressSchema.nullish(),
  receipt: z.any().optional(),
  status: FulfillmentStatusEnum,
  service: z.string(),
  shipment_status: ShipmentStatusEnum.nullish(),
  tracking_company: z.string().nullish(),
  tracking_number: z.string().nullish(),
  tracking_numbers: z.array(z.string()),
  tracking_url: z.string().nullish(),
  tracking_urls: z.union([z.array(z.string()).nullish(), z.null()]), // null for CS orders
  updated_at: iso8601DateString,
  variant_inventory_management: z.string().nullish(),
});

const MarketingConsentSchema = z.object({
  state: z.string(),
  opt_in_level: z.string().nullish(),
  consent_updated_at: iso8601DateString.nullish(),
  consent_collected_from: z.string().nullish(),
});

const CustomerStateSchema = z.enum([
  "disabled",
  "invited",
  "enabled",
  "declined",
]);

const CustomerSchema = z.object({
  accepts_marketing: z.boolean().nullish(),
  accepts_marketing_updated_at: iso8601DateString.nullish(),
  addresses: z.array(CustomerAddressSchema).nullish(),
  admin_graphql_api_id: z.string().nullish(),
  created_at: iso8601DateString.nullish(),
  currency: z.string(),
  default_address: CustomerAddressSchema.merge(
    z.object({
      default: z.boolean(),
      id: z.number(),
      country_name: z.string().nullish(),
      customer_id: z.number(),
    }),
  ).nullish(),
  email: z.string().nullish(),
  email_marketing_consent: MarketingConsentSchema.nullish(),
  id: z.union([z.number(), z.string()]),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  last_order_id: z.number().nullish(),
  last_order_name: z.string().nullish(),
  metafield: z
    .object({
      key: z.string(),
      value: z.string(),
      value_type: z.string(),
      namespace: z.string(),
    })
    .nullish(),
  marketing_opt_in_level: z.string().nullish(), // deprecated, use email_marketing_consent
  multipass_identifier: z.string().nullish(),
  note: z.string().nullish(),
  orders_count: z.number().nullish(),
  password: z.string().nullish(),
  password_confirmation: z.string().nullish(),
  phone: z.string().nullish(),
  sms_marketing_consent: MarketingConsentSchema.nullish(),
  state: CustomerStateSchema,
  tags: z.string(),
  tax_exempt: z.boolean(),
  tax_exemptions: z.array(z.string()),
  total_spent: z.string().nullish(),
  updated_at: iso8601DateString.nullish(),
  verified_email: z.boolean(),
});

const PaymentTermsSchema = z.object({
  id: z.number(),
  amount: z.number().nullish(),
  currency: z.string().nullish(),
  payment_terms_name: z.string(),
  payment_terms_type: z.string(),
  created_at: iso8601DateString,
  updated_at: iso8601DateString,
  due_in_days: z.number().nullish(),
  payment_schedules: z.array(
    z.object({
      id: z.number(),
      amount: z.string(),
      currency: z.string(),
      issued_at: iso8601DateString.nullish(),
      due_at: iso8601DateString.nullish(),
      completed_at: iso8601DateString.nullish(),
      expected_payment_method: z.string().nullish(),
      created_at: iso8601DateString,
      updated_at: iso8601DateString,
    }),
  ),
});

const ShippingLineSchema = z.object({
  id: z.number(),
  delivery_category: z.string().nullish(),
  phone: z.string().nullish(),
  discount_allocations: z.array(
    z.object({
      amount: z.string(),
      discount_application_index: z.number(),
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  code: z.string(),
  discounted_price: z.string(),
  discounted_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  price: z.string(),
  price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  source: z.string().nullish(),
  title: z.string(),
  tax_lines: z.array(TaxLineSchema),
  carrier_identifier: z.string().nullish(),
  requested_fulfillment_service_id: z.string().nullish(),
  is_removed: z.boolean().nullish(),
});

const RefundOrderAdjustmentKindEnum = z.enum([
  "shipping_refund",
  "refund_discrepancy",
]);
const RefundDutyTypeEnum = z.enum(["FULL", "PROPORTIONAL"]);
const RefundLineItemRestockTypeEnum = z.enum([
  "no_restock",
  "cancel",
  "return",
  "legacy_restock",
]);
const TransactionErrorCodeEnum = z.enum([
  "incorrect_number",
  "invalid_number",
  "invalid_expiry_date",
  "invalid_cvc",
  "expired_card",
  "incorrect_cvc",
  "incorrect_zip",
  "incorrect_address",
  "card_declined",
  "processing_error",
  "call_issuer",
  "pick_up_card",
  "generic_error",
]);
const TransactionKindEnum = z.enum([
  "authorization",
  "capture",
  "sale",
  "void",
  "refund",
]);
const TransactionStatusEnum = z.enum([
  "pending",
  "error",
  "success",
  "failure",
  "deferred",
]);

const PaymentDetailsSchema = z.object({
  credit_card_bin: z.string().nullish(),
  avs_result_code: z.string().nullish(),
  cvv_result_code: z.string().nullish(),
  credit_card_number: z.string(),
  credit_card_company: z.string(),
  credit_card_name: z.string().nullish(),
  credit_card_wallet: z.string().nullish(),
  credit_card_expiration_month: z.number().nullish(),
  credit_card_expiration_year: z.number().nullish(),
  buyer_action_info: z.object({}).nullish(),
  payment_method_name: z.string().nullish(),
});

const TransactionSchema = z.object({
  admin_graphql_api_id: z.string(),
  amount: z.string(),
  authorization: z.string().nullish(),
  authorization_expires_at: iso8601DateString.nullish(),
  created_at: iso8601DateString,
  currency: z.string().nullish(),
  device_id: z.number().nullish(),
  error_code: TransactionErrorCodeEnum.nullish(),
  extended_authorization_attributes: z
    .object({
      standard_authorization_expires_at: iso8601DateString,
      extended_authorization_expires_at: iso8601DateString.nullish(),
    })
    .nullish(),
  gateway: z.string().nullish(),
  id: z.number(),
  kind: TransactionKindEnum,
  location_id: z.number().nullish(),
  message: z.string().nullish(),
  order_id: z.number(),
  payment_details: PaymentDetailsSchema.nullish(),
  parent_id: z.number().nullish(),
  payment_id: z.string().nullish(),
  payments_refund_attributes: z
    .object({
      status: TransactionStatusEnum,
      acquirer_reference_number: z.string().nullish(),
    })
    .nullish(),
  processed_at: iso8601DateString,
  receipt: z.any(),
  source_name: z.string(),
  status: TransactionStatusEnum,
  total_unsettled_set: OptionalMoneySetSchema,
  test: z.boolean(),
  user_id: z.number().nullish(),
  currenct_exchange_adjustment: z
    .object({
      id: z.number(),
      adjustment: z.string(),
      original_amount: z.string(),
      final_amount: z.string(),
      currency: z.string(),
    })
    .nullish(),
});

const RefundSchema = z.object({
  admin_graphql_api_id: z.string(),
  order_id: z.number(),
  total_duties_set: OptionalMoneySetSchema,
  created_at: iso8601DateString,
  duties: z.array(
    z.object({
      duty_id: z.number(),
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  id: z.number(),
  note: z.string().nullish(),
  order_adjustments: z.array(
    z.object({
      id: z.number(),
      order_id: z.number(),
      refund_id: z.number(),
      amount: z.string(),
      tax_amount: z.string(),
      reason: z.string(),
      kind: RefundOrderAdjustmentKindEnum,
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
      tax_amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  processed_at: iso8601DateString,
  refund_duties: z
    .array(z.object({ duty_id: z.number(), refund_type: RefundDutyTypeEnum }))
    .nullish(),
  refund_line_items: z.array(
    z.object({
      id: z.number(),
      line_item: LineItemSchema,
      line_item_id: z.number(),
      quantity: z.number(),
      restock_type: RefundLineItemRestockTypeEnum.nullish(),
      location_id: z.number().nullish(),
      subtotal: z.number(),
      total_tax: z.number(),
      subtotal_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
      total_tax_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  restock: z.boolean(),
  transactions: z.array(TransactionSchema),
  user_id: z.number().nullish(),
});

const SparceShopifyRefund = z.object({
  refund_line_items: z.array(
    z.object({ line_item_id: z.number(), quantity: z.number() }),
  ),
});

const CancelledReasonEnum = z.enum([
  "customer",
  "fraud",
  "inventory",
  "declined",
  "other",
  "staff",
]);
const FinancialStatusEnum = z.enum([
  "authorized",
  "pending",
  "paid",
  "partially_paid",
  "partially_refunded",
  "refunded",
  "voided",
]);

/**
 * @note The separately defined ShopifyOrder type is necessary to prevent
 * "excessively deep types."
 */
export const ShopifyOrderSchema: z.ZodType<ShopifyOrder> = z.object({
  admin_graphql_api_id: z.string().nullish(),
  app_id: z.number().nullish(),
  billing_address: CustomerAddressSchema.nullish(),
  browser_ip: z.string().nullish(),
  buyer_accepts_marketing: z.boolean(),
  cancel_reason: CancelledReasonEnum.nullish(),
  cancelled_at: iso8601DateString.nullish(),
  cart_token: z.string().nullish(),
  checkout_id: z.number().nullish(),
  checkout_token: z.string().nullish(),
  client_details: ClientDetailsSchema.nullish(),
  closed_at: iso8601DateString.nullish(),
  company: z
    .object({ id: z.number(), location_id: z.number().nullish() })
    .nullish(),
  confirmation_number: z.string().nullish(),
  confirmed: z.boolean(),
  contact_email: z.string().nullish(),
  created_at: iso8601DateString,
  currency: z.string(),
  current_total_additional_fees_set: OptionalMoneySetSchema,
  current_total_discounts: z.string(),
  current_total_discounts_set: OptionalMoneySetSchema,
  current_total_duties_set: OptionalMoneySetSchema,
  current_total_price: z.string(),
  current_total_price_set: OptionalMoneySetSchema,
  current_subtotal_price: z.string(),
  current_subtotal_price_set: OptionalMoneySetSchema,
  current_total_tax: z.string(),
  current_total_tax_set: OptionalMoneySetSchema,
  customer: CustomerSchema.nullish(),
  customer_locale: z.string().nullish(),
  device_id: z.number().nullish(),
  discount_applications: z.array(DiscountApplicationSchema),
  discount_codes: z.array(DiscountCodeSchema),
  email: z.string().nullish(),
  estimated_taxes: z.boolean().nullish(),
  financial_status: FinancialStatusEnum.nullish(),
  fulfillments: z.array(FulfillmentSchema),
  fulfillment_status: OrderFulfillmentStatusEnum.nullish(),
  gateway: z.string().nullish(),
  id: z.union([z.number(), z.string()]),
  landing_site: z.string().nullish(),
  landing_site_ref: z.string().nullish(),
  line_items: z.array(LineItemSchema),
  location_id: z.number().nullish(),
  merchant_of_record_app_id: z.number().nullish(),
  name: z.string(),
  note: z.string().nullish(),
  note_attributes: z.array(PropertySchema),
  number: z.union([z.number(), z.string()]),
  order_number: z.union([z.number(), z.string()]).nullish(),
  original_total_additional_fees_set: OptionalMoneySetSchema,
  original_total_duties_set: OptionalMoneySetSchema,
  payment_details: PaymentDetailsSchema.nullish(),
  payment_terms: PaymentTermsSchema.nullish(),
  payment_gateway_names: z.array(z.string()),
  phone: z.string().nullish(),
  po_number: z.string().nullish(),
  presentment_currency: z.string(),
  processing_method: z.string().nullish(),
  processed_at: iso8601DateString,
  reference: z.string().nullish(),
  referring_site: z.string().nullish(),
  refunds: z.union([z.array(RefundSchema), z.array(SparceShopifyRefund)]),
  shipping_address: CustomerAddressSchema.nullish(),
  shipping_lines: z.array(ShippingLineSchema),
  source_identifier: z.string().nullish(),
  source_name: z.string(),
  source_url: z.string().nullish(),
  subtotal_price: z.string(),
  subtotal_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  tags: z.string().nullish(),
  tax_exempt: z.boolean().nullish(),
  tax_lines: z.array(TaxLineSchema),
  taxes_included: z.boolean(),
  test: z.boolean(),
  token: z.string(),
  total_discounts: z.string(),
  total_discounts_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_line_items_price: z.string(),
  total_line_items_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_outstanding: z.string(),
  total_price: z.string(),
  total_price_usd: z.string().nullish(),
  total_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_shipping_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_tax: z.string(),
  total_tax_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_tip_received: z.string(),
  total_weight: z.number().nullish(),
  updated_at: iso8601DateString,
  user_id: z.number().nullish(),
  order_status_url: urlSchema.nullish(),
  original_order: z.any().nullish(),
}) as any;

/**
 * TypeScript interfaces mirroring the Zod schemas
 */
export interface MoneySet {
  amount: string;
  currency_code: string;
}

export interface OptionalMoneySet {
  shop_money?: MoneySet;
  presentment_money?: MoneySet;
}

export interface ClientDetails {
  accept_language?: string | null;
  browser_height?: number | null;
  browser_ip?: string | null;
  browser_width?: number | null;
  session_hash?: string | null;
  user_agent?: string | null;
}

export interface Property {
  name?: string | null;
  value?: any | null;
}

export interface PaymentDetails {
  credit_card_bin?: string | null;
  avs_result_code?: string | null;
  cvv_result_code?: string | null;
  credit_card_number: string;
  credit_card_company: string;
  credit_card_name?: string | null;
  credit_card_wallet?: string | null;
  credit_card_expiration_month?: number | null;
  credit_card_expiration_year?: number | null;
  buyer_action_info?: Record<string, never> | null;
  payment_method_name?: string | null;
}

export interface TaxLine {
  title: string;
  price: string;
  rate: number;
  price_set?: { shop_money: MoneySet; presentment_money: MoneySet } | null;
  channel_liable?: boolean | null;
}

export interface DiscountAllocation {
  amount: string;
  discount_application_index: number;
  amount_set: { shop_money: MoneySet; presentment_money: MoneySet };
}

export interface OriginAddress {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  country_code?: string | null;
  province_code?: string | null;
  zip?: string | null;
}

export interface DestinationLocation {
  id: number;
  country_code?: string | null;
  province_code?: string | null;
  name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  zip?: string | null;
}

export interface OriginLocation {
  id: number;
  country_code: string;
  province_code: string;
  name: string;
  address1: string;
  address2?: string | null;
  city: string;
  zip: string;
}

export interface MarketingConsent {
  state: string;
  opt_in_level?: string | null;
  consent_updated_at?: string | null;
  consent_collected_from?: string | null;
}

export interface PaymentTerms {
  id: number;
  amount?: number | null;
  currency?: string | null;
  payment_terms_name: string;
  payment_terms_type: string;
  created_at: string;
  updated_at: string;
  due_in_days?: number | null;
  payment_schedules: Array<{
    id: number;
    amount: string;
    currency: string;
    issued_at?: string | null;
    due_at?: string | null;
    completed_at?: string | null;
    expected_payment_method?: string | null;
    created_at: string;
    updated_at: string;
  }>;
}

export interface ShippingLine {
  id: number;
  delivery_category?: string | null;
  phone?: string | null;
  discount_allocations: DiscountAllocation[];
  code: string;
  discounted_price: string;
  discounted_price_set: { shop_money: MoneySet; presentment_money: MoneySet };
  price: string;
  price_set: { shop_money: MoneySet; presentment_money: MoneySet };
  source?: string | null;
  title: string;
  tax_lines: TaxLine[];
  carrier_identifier?: string | null;
  requested_fulfillment_service_id?: string | null;
  is_removed?: boolean | null;
}

export interface Transaction {
  admin_graphql_api_id: string;
  amount: string;
  authorization?: string | null;
  authorization_expires_at?: string | null;
  created_at: string;
  currency?: string | null;
  device_id?: number | null;
  error_code?:
    | "incorrect_number"
    | "invalid_number"
    | "invalid_expiry_date"
    | "invalid_cvc"
    | "expired_card"
    | "incorrect_cvc"
    | "incorrect_zip"
    | "incorrect_address"
    | "card_declined"
    | "processing_error"
    | "call_issuer"
    | "pick_up_card"
    | "generic_error"
    | null;
  extended_authorization_attributes?: {
    standard_authorization_expires_at: string;
    extended_authorization_expires_at?: string | null;
  } | null;
  gateway?: string | null;
  id: number;
  kind: "authorization" | "capture" | "sale" | "void" | "refund";
  location_id?: number | null;
  message?: string | null;
  order_id: number;
  payment_details?: PaymentDetails | null;
  parent_id?: number | null;
  payment_id?: string | null;
  payments_refund_attributes?: {
    status: "pending" | "error" | "success" | "failure" | "deferred";
    acquirer_reference_number?: string | null;
  } | null;
  processed_at: string;
  receipt: any;
  source_name: string;
  status: "pending" | "error" | "success" | "failure" | "deferred";
  total_unsettled_set?: OptionalMoneySet | null;
  test: boolean;
  user_id?: number | null;
  currenct_exchange_adjustment?: {
    id: number;
    adjustment: string;
    original_amount: string;
    final_amount: string;
    currency: string;
  } | null;
}

export interface LineItem {
  id: number | string;
  current_quantity?: number | null;
  tip_payment_gateway?: string | null;
  tip_payment_method?: string | null;
  image?: {
    id: number;
    product_id: number | string;
    position: number;
    created_at: string;
    updated_at: string;
    alt?: string | null;
    width?: number | null;
    height?: number | null;
    src: string;
    variant_ids: number[];
    admin_graphql_api_id: string;
  } | null;
  protected?: boolean | string[] | null;
  finalSaleProtected?: boolean | null;
  tags?: string[] | null;
  tip?: {
    payment_method?: string | null;
    payment_gateway?: string | null;
  } | null;
  attributed_staffs?: { id: string; quantity: number }[] | null;
  redoEligible?: boolean | null;
  redoPackageProtectionEligible?: boolean | null;
  admin_graphql_api_id: string;
  variant_id?: number | string | null;
  title: string;
  quantity: number;
  price: string;
  price_set?: OptionalMoneySet | null;
  pre_tax_price?: string | null;
  pre_tax_price_set?: OptionalMoneySet | null;
  grams: number;
  sku?: string | null;
  vendor?: string | null;
  variant_title?: string | null;
  fulfillment_service: string;
  product_id?: number | string | null;
  requires_shipping: boolean;
  taxable: boolean;
  gift_card: boolean;
  name: string;
  variant_inventory_management?: string | null;
  properties: Property[];
  product_exists: boolean;
  fulfillable_quantity: number;
  total_discount: string;
  fulfillment_status?:
    | "fulfilled"
    | "unfulfilled"
    | "restocked"
    | "partial"
    | "not_eligible"
    | null;
  total_discount_set: { shop_money: MoneySet; presentment_money: MoneySet };
  destination_location?: DestinationLocation | null;
  origin_location?: OriginLocation | null;
  discount_allocations: DiscountAllocation[];
  fulfillment_line_item_id?: string | null;
  tax_code?: string | null;
  tax_lines: TaxLine[];
  duties: {
    id: number;
    harmonized_system_code?: string | null;
    country_code_of_origin?: string | null;
    price_set?: OptionalMoneySet | null;
    tax_lines: TaxLine[];
    admin_graphql_api_id: string;
  }[];
  discount_price?: string | null;
  reason?: string | null;
  loyaltyAmount?: number | null;
  duties_included?: any;
  merchant_business_entity_id?: any;
  total_cash_rounding_payment_adjustment_set?: any;
  total_cash_rounding_refund_adjustment_set?: any;
}

export interface Fulfillment {
  admin_graphql_api_id?: string | null;
  created_at: string;
  id: number | string | null; // null for CS orders
  line_items: LineItem[];
  location_id: number;
  location?: { name?: string | null } | null;
  name: string;
  order_id: number;
  origin_address?: OriginAddress | null;
  receipt?: any;
  status: "pending" | "open" | "success" | "cancelled" | "error" | "failure";
  service: string;
  shipment_status?:
    | "label_printed"
    | "label_purchased"
    | "attempted_delivery"
    | "ready_for_pickup"
    | "confirmed"
    | "in_transit"
    | "out_for_delivery"
    | "delivered"
    | "failure"
    | "picked_up"
    | "stalled_in_transit"
    | "delayed"
    | null;
  tracking_company?: string | null;
  tracking_number?: string | null;
  tracking_numbers: string[];
  tracking_url?: string | null;
  tracking_urls: string[] | null; // null for CS orders
  updated_at: string;
  variant_inventory_management?: string | null;
}

export interface Refund {
  admin_graphql_api_id: string;
  order_id: number;
  total_duties_set?: OptionalMoneySet | null;
  created_at: string;
  duties: {
    duty_id: number;
    amount_set: { shop_money: MoneySet; presentment_money: MoneySet };
  }[];
  id: number;
  note?: string | null;
  order_adjustments: {
    id: number;
    order_id: number;
    refund_id: number;
    amount: string;
    tax_amount: string;
    reason: string;
    kind: "shipping_refund" | "refund_discrepancy";
    amount_set: { shop_money: MoneySet; presentment_money: MoneySet };
    tax_amount_set: { shop_money: MoneySet; presentment_money: MoneySet };
  }[];
  processed_at: string;
  refund_duties?:
    | { duty_id: number; refund_type: "FULL" | "PROPORTIONAL" }[]
    | null;
  refund_line_items: {
    id: number;
    line_item: LineItem;
    line_item_id: number;
    quantity: number;
    restock_type?: "no_restock" | "cancel" | "return" | "legacy_restock" | null;
    location_id?: number | null;
    subtotal: number;
    total_tax: number;
    subtotal_set: { shop_money: MoneySet; presentment_money: MoneySet };
    total_tax_set: { shop_money: MoneySet; presentment_money: MoneySet };
  }[];
  restock: boolean;
  transactions: Transaction[];
  user_id?: number | null;
}

export interface DiscountCode {
  amount: string;
  code: string;
  type: "fixed_amount" | "percentage" | "shipping";
}

export interface DiscountApplication {
  allocation_method: "across" | "each" | "one";
  code?: string | null;
  description?: string | null;
  target_selection: "all" | "entitled" | "explicit";
  target_type: "line_item" | "shipping_line";
  title?: string | null;
  type: "automatic" | "manual" | "script" | "discount_code";
  value_type: "fixed_amount" | "percentage";
  value: string;
}

export interface CustomerAddressInterface {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  company?: string | null;
  country?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  province?: string | null;
  zip?: string | null;
  name?: string | null;
  province_code?: string | null;
  country_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface Customer {
  accepts_marketing?: boolean | null;
  accepts_marketing_updated_at?: string | null;
  addresses?: CustomerAddressInterface[] | null;
  admin_graphql_api_id?: string | null;
  created_at: string;
  currency: string;
  default_address?:
    | (CustomerAddressInterface & {
        default: boolean;
        id: number;
        country_name?: string | null;
        customer_id: number;
      })
    | null;
  email?: string | null;
  email_marketing_consent?: MarketingConsent | null;
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  last_order_id?: number | null;
  last_order_name?: string | null;
  metafield?: {
    key: string;
    value: string;
    value_type: string;
    namespace: string;
  } | null;
  marketing_opt_in_level?: string | null;
  multipass_identifier?: string | null;
  note?: string | null;
  orders_count?: number | null;
  password?: string | null;
  password_confirmation?: string | null;
  phone?: string | null;
  sms_marketing_consent?: MarketingConsent | null;
  state: "disabled" | "invited" | "enabled" | "declined";
  tags: string;
  tax_exempt: boolean;
  tax_exemptions: string[];
  total_spent?: string | null;
  updated_at: string;
  verified_email: boolean;
}

export interface ShopifyOrder {
  admin_graphql_api_id?: string | null;
  app_id?: number | null;
  billing_address?: CustomerAddressInterface | null;
  browser_ip?: string | null;
  buyer_accepts_marketing: boolean;
  cancel_reason?:
    | "customer"
    | "fraud"
    | "inventory"
    | "declined"
    | "other"
    | "staff"
    | null;
  cancelled_at?: string | null;
  cart_token?: string | null;
  checkout_id?: number | null;
  checkout_token?: string | null;
  client_details?: ClientDetails | null;
  closed_at?: string | null;
  company?: { id: number; location_id?: number | null } | null;
  confirmation_number?: string | null;
  confirmed: boolean;
  contact_email?: string | null;
  created_at: string;
  currency: string;
  current_total_additional_fees_set?: OptionalMoneySet | null;
  current_total_discounts: string;
  current_total_discounts_set?: OptionalMoneySet | null;
  current_total_duties_set?: OptionalMoneySet | null;
  current_total_price: string;
  current_total_price_set?: OptionalMoneySet | null;
  current_subtotal_price: string;
  current_subtotal_price_set?: OptionalMoneySet | null;
  current_total_tax: string;
  current_total_tax_set?: OptionalMoneySet | null;
  customer?: Customer | null;
  customer_locale?: string | null;
  device_id?: number | null;
  discount_applications: DiscountApplication[];
  discount_codes: DiscountCode[];
  email?: string | null;
  estimated_taxes?: boolean | null;
  financial_status?:
    | "authorized"
    | "pending"
    | "paid"
    | "partially_paid"
    | "partially_refunded"
    | "refunded"
    | "voided"
    | null;
  fulfillments: Fulfillment[];
  fulfillment_status?:
    | "fulfilled"
    | "unfulfilled"
    | "restocked"
    | "partial"
    | "not_eligible"
    | null;
  gateway?: string | null;
  id: number | string;
  landing_site?: string | null;
  landing_site_ref?: string | null;
  line_items: LineItem[];
  location_id?: number | null;
  merchant_of_record_app_id?: number | null;
  name: string;
  note?: string | null;
  note_attributes: Property[];
  number: number | string;
  order_number?: number | string | null;
  original_total_additional_fees_set?: OptionalMoneySet | null;
  original_total_duties_set?: OptionalMoneySet | null;
  payment_details?: PaymentDetails | null;
  payment_terms?: PaymentTerms | null;
  payment_gateway_names: string[];
  phone?: string | null;
  po_number?: string | null;
  presentment_currency: string;
  processing_method?: string | null;
  processed_at: string;
  reference?: string | null;
  referring_site?: string | null;
  refunds: Refund[];
  shipping_address?: CustomerAddressInterface | null;
  shipping_lines: ShippingLine[];
  source_identifier?: string | null;
  source_name: string;
  source_url?: string | null;
  subtotal_price: string;
  subtotal_price_set: { shop_money: MoneySet; presentment_money: MoneySet };
  tags?: string | null;
  tax_exempt?: boolean | null;
  tax_lines: TaxLine[];
  taxes_included: boolean;
  test: boolean;
  token: string;
  total_discounts: string;
  total_discounts_set: { shop_money: MoneySet; presentment_money: MoneySet };
  total_line_items_price: string;
  total_line_items_price_set: {
    shop_money: MoneySet;
    presentment_money: MoneySet;
  };
  total_outstanding: string;
  total_price: string;
  total_price_usd?: string | null;
  total_price_set: { shop_money: MoneySet; presentment_money: MoneySet };
  total_shipping_price_set: {
    shop_money: MoneySet;
    presentment_money: MoneySet;
  };
  total_tax: string;
  total_tax_set: { shop_money: MoneySet; presentment_money: MoneySet };
  total_tip_received: string;
  total_weight?: number | null;
  updated_at: string;
  user_id?: number | null;
  order_status_url?: string | null;
  original_order?: any | null;
}
